import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import PartnersStrip from "@/components/PartnersStrip";
import Footer from "@/components/layout/Footer";
import { Search, MapPin, ExternalLink, Instagram, Linkedin, Globe, Lock, Crown, Star, Mail, Users, Sparkles } from "lucide-react";
import { PinButton } from "@/components/PinContext";
import { MatchButton } from "@/components/MatchContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const MemberDirectory = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSkill, setSelectedSkill] = useState("");
  const [matchMode, setMatchMode] = useState(false);
  const [matchQuery, setMatchQuery] = useState("");
  const [isMatchDialogOpen, setIsMatchDialogOpen] = useState(false);

  const matchOptions = [
    "Looking for a Designer",
    "Looking for a Writer",
    "Looking for a Photographer",
    "Looking for a Filmmaker",
    "Looking for a Strategist",
    "Looking for a Developer",
    "Looking for a Marketing Expert",
    "Looking for a Brand Partner",
    "Looking for a Mentor",
    "Hiring for a role",
    "Looking for collaborators",
  ];

  useEffect(() => {
    axios.get(`${API}/members?limit=50`)
      .then((res) => { setMembers(res.data); setFilteredMembers(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));

    axios.get(`${API}/members/skills/all`)
      .then((res) => setSkills(res.data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    let filtered = [...members];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.name?.toLowerCase().includes(query) ||
          m.title?.toLowerCase().includes(query) ||
          m.company?.toLowerCase().includes(query) ||
          m.industry?.toLowerCase().includes(query) ||
          m.bio?.toLowerCase().includes(query) ||
          m.skills?.some(s => s.toLowerCase().includes(query)) ||
          m.goals?.some(g => g.toLowerCase().includes(query))
      );
    }
    if (selectedSkill) {
      // Substring match so e.g. clicking "Writing" finds anyone with "Creative Writing", "Copywriting", etc.
      const sLower = selectedSkill.toLowerCase();
      filtered = filtered.filter((m) =>
        m.skills?.some((s) => {
          const v = s.toLowerCase();
          return v === sLower || v.includes(sLower) || sLower.includes(v);
        })
      );
    }
    setFilteredMembers(filtered);
  }, [searchQuery, selectedSkill, members]);

  const handleMatch = (option) => {
    setIsMatchDialogOpen(false);
    setMatchMode(true);
    // Extract the role/skill keyword from the option
    const keyword = option.replace("Looking for a ", "").replace("Looking for ", "").replace("Hiring for a role", "").toLowerCase();
    setSearchQuery(keyword);
    setMatchQuery(option);
  };

  const clearMatch = () => {
    setMatchMode(false);
    setSearchQuery("");
    setMatchQuery("");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="member-directory-page">
      <Navbar />
      <DashboardHeader />

      {/* Section heading — slim, contained */}
      <section className="pt-8 md:pt-10 pb-2">
        <div className="container-main">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="overline text-[#FF2D87] mb-2">Member Directory</p>
              <p className="text-sm md:text-base text-[#4A4A4A] max-w-2xl">
                Connect with female and gender diverse creatives and professionals across industries — collaborators, mentors, and friends who understand your journey.
              </p>
            </div>

            {user && (
              <Dialog open={isMatchDialogOpen} onOpenChange={setIsMatchDialogOpen}>
                <DialogTrigger asChild>
                  <button className="bg-[#0A0A0A] text-white font-bold rounded-full px-5 py-2.5 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="match-me-btn">
                    <Sparkles className="w-4 h-4" /> Find your match
                  </button>
                </DialogTrigger>
                <DialogContent className="dialog-content sm:max-w-lg" data-testid="match-modal">
                  <DialogHeader className="mb-1">
                    <DialogTitle className="text-3xl font-display font-bold text-[#0A0A0A] leading-tight">What are you<br />looking for?</DialogTitle>
                  </DialogHeader>
                  <p className="text-[#4A4A4A] text-sm mb-6">Tell us what you need and we'll show you matching members.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {matchOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleMatch(option)}
                        className="text-left px-4 py-3 bg-white border-2 border-[#0A0A0A] hover:bg-[#FF2D87] hover:text-white hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all text-sm font-bold rounded-none"
                        data-testid={`match-option-${option.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {option.replace("Looking for a ", "").replace("Looking for ", "").replace("Hiring for a role", "Hiring")}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 pt-5 border-t border-[#E5E5E5]">
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4A4A4A] mb-2">Or describe in your own words</label>
                    <input
                      type="text"
                      placeholder="e.g. PR consultant, voice actor, copywriter…"
                      className="w-full px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] text-sm"
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) handleMatch(e.target.value); }}
                      data-testid="match-custom-input"
                    />
                    <p className="text-[10px] text-[#4A4A4A] mt-2">Press Enter to search</p>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </section>

      {/* Match Banner */}
      {matchMode && (
        <section className="bg-[#0A0A0A] py-4">
          <div className="container-main flex items-center justify-between flex-wrap gap-3">
            <p className="text-white text-sm">
              <Sparkles className="w-4 h-4 inline mr-2 text-[#FF2D87]" />
              Showing matches for: <span className="font-bold text-[#FF2D87]">{matchQuery}</span>
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/businesses?q=${encodeURIComponent(searchQuery)}`}
                className="text-[#FFC2E8] hover:text-white text-xs font-bold uppercase tracking-widest underline"
                data-testid="match-search-businesses"
              >
                Also search businesses →
              </Link>
              <button onClick={clearMatch} className="text-white/70 hover:text-white text-sm font-bold">Clear</button>
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="py-8 border-b-2 border-[#E5E5E5]">
        <div className="container-main">
          <div className="relative max-w-2xl mx-auto md:mx-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A4A]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, title, company, industry, skill, goal, or bio…"
              className="w-full pl-12 pr-4 py-3 border-2 border-[#0A0A0A] focus:outline-none focus:border-[#5170FF]"
              data-testid="member-search-input"
            />
          </div>

          {skills.length > 0 && (
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A] mb-3">Filter by skill</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedSkill("")}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border-2 transition-colors ${
                    !selectedSkill
                      ? "bg-[#FF2D87] border-[#FF2D87] text-white"
                      : "bg-transparent border-[#FF2D87] text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white"
                  }`}
                  data-testid="skill-filter-all"
                >
                  All Skills
                </button>
                {skills.map((skill) => (
                  <button
                    key={skill}
                    onClick={() => setSelectedSkill(selectedSkill === skill ? "" : skill)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border-2 transition-colors ${
                      selectedSkill === skill
                        ? "bg-[#FF2D87] border-[#FF2D87] text-white"
                        : "bg-transparent border-[#FF2D87] text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white"
                    }`}
                    data-testid={`skill-filter-${skill}`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Members Grid */}
      <section className="py-10 md:py-14">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-[#0A0A0A] p-6 rounded-sm">
                  <div className="skeleton w-24 h-24 rounded-full mx-auto mb-4" />
                  <div className="skeleton h-6 w-3/4 mx-auto mb-2" />
                  <div className="skeleton h-4 w-1/2 mx-auto" />
                </div>
              ))}
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-20">
              {!user ? (
                <>
                  <Lock className="w-16 h-16 text-[#FFC2E8] mx-auto mb-4" />
                  <h2 className="text-2xl font-display font-bold mb-4">Founding members are shown publicly</h2>
                  <p className="text-[#4A4A4A] mb-6 max-w-md mx-auto">
                    Sign up to browse the full member directory and connect with our creative community.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Link to="/login" className="btn-primary" data-testid="directory-join-btn">
                      Join to See All Members
                    </Link>
                    <Link to="/login" className="btn-secondary">Login</Link>
                  </div>
                </>
              ) : matchMode ? (
                <>
                  <Users className="w-16 h-16 text-[#FFC2E8] mx-auto mb-4" />
                  <h2 className="text-2xl font-display font-bold mb-4">No matches yet</h2>
                  <p className="text-[#4A4A4A] mb-4">We're growing! No members match this criteria yet.</p>
                  <button onClick={clearMatch} className="btn-primary">Show All Members</button>
                </>
              ) : (
                <>
                  <p className="text-xl text-[#4A4A4A]">No members found</p>
                  <p className="text-[#4A4A4A] mt-2">Try adjusting your search or filters</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                {filteredMembers.map((member) => (
                  <MemberCard key={member.user_id} member={member} currentUser={user} />
                ))}
              </div>

              {/* Login prompt for non-authenticated */}
              {!user && (
                <div className="mt-16 bg-[#FFC2E8] border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-12 text-center" data-testid="directory-login-prompt">
                  <Lock className="w-10 h-10 text-[#FF2D87] mx-auto mb-4" />
                  <h3 className="text-2xl font-display font-bold mb-3">See the full directory</h3>
                  <p className="text-[#0A0A0A] mb-6 max-w-md mx-auto">
                    Sign up for free to browse all members, connect, and collaborate.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Link to="/login" className="btn-primary" data-testid="directory-signup-btn">Join The Ensemble</Link>
                    <Link to="/login" className="btn-secondary">Login</Link>
                  </div>
                </div>
              )}

              {/* Upgrade prompt for free members (founders/founding members already have full access) */}
              {user && user.membership_tier === "free" && !user.is_founding_member && !user.is_founder && !user.is_admin && (
                <div className="mt-16 bg-[#0A0A0A] p-8 text-center text-white rounded-sm" data-testid="upgrade-prompt">
                  <Mail className="w-8 h-8 text-[#FF2D87] mx-auto mb-3" />
                  <h3 className="text-xl font-display font-bold mb-2">Want to connect directly?</h3>
                  <p className="text-white/70 mb-4 max-w-md mx-auto text-sm">
                    Contact info is available for paid members. Upgrade to message members directly.
                  </p>
                  <span className="text-sm font-bold uppercase tracking-wider text-[#FFC2E8]">Paid tiers launching soon</span>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <PartnersStrip />
      <Footer />
    </div>
  );
};

const MemberCard = ({ member, currentUser }) => {
  const viewerHasFullAccess = currentUser && (
    currentUser.membership_tier !== "free"
    || currentUser.is_founding_member
    || currentUser.is_founder
    || currentUser.is_admin
  );
  const canSeeContact = viewerHasFullAccess || member.is_founding_member;
  const isPaidMember = viewerHasFullAccess;

  const handleEmailClick = () => {
    if (!currentUser) {
      toast.error("Please sign in to connect with members");
    } else if (!isPaidMember && !member.is_founding_member) {
      toast.info("Upgrade to a paid membership to connect directly");
    }
  };

  return (
    <div className="bg-[#0A0A0A] rounded-sm p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl flex flex-col h-full relative" data-testid={`member-card-${member.user_id}`}>
      {currentUser && currentUser.user_id !== member.user_id && (
        <div className="absolute top-3 right-3 z-10 flex gap-1">
          <MatchButton targetUserId={member.user_id} className="w-8 h-8 rounded-full" />
          <PinButton targetType="member" targetId={member.user_id} className="w-8 h-8 rounded-full" />
        </div>
      )}
      <div className="flex flex-col items-center text-center flex-1">
        {/* Avatar */}
        <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-[#FF2D87] mb-4">
          {member.picture ? (
            <img src={member.picture?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${member.picture}` : member.picture}
              alt={member.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#FF2D87] flex items-center justify-center text-white text-2xl font-bold">
              {member.name?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Badges */}
        <div className="flex gap-2 mb-2 min-h-[20px]">
          {member.is_founder && (
            <span className="text-[9px] font-bold uppercase tracking-widest bg-[#FF2D87] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
              <Star className="w-2.5 h-2.5" /> Founder
            </span>
          )}
          {member.is_founding_member && !member.is_founder && (
            <span className="text-[9px] font-bold uppercase tracking-widest bg-[#FFC2E8] text-[#0A0A0A] px-2 py-0.5 rounded-full flex items-center gap-1">
              <Crown className="w-2.5 h-2.5" /> Founding Member
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="text-lg font-display font-bold text-[#FF2D87] mb-1">{member.name}</h3>

        {/* Title @ Company */}
        {(member.title || member.company) && (
          <p className="text-sm text-white mb-2 px-2">
            {member.title}
            {member.title && member.company && <span className="text-[#5170FF] font-bold mx-1.5">@</span>}
            {member.company && <span className="font-semibold">{member.company}</span>}
          </p>
        )}

        {/* Location - Blue */}
        {member.location && (
          <div className="flex items-center gap-1 text-xs text-[#5170FF] mb-3">
            <MapPin className="w-3 h-3" />
            {member.location}
          </div>
        )}

        {/* Skills - Bright pink bubbles */}
        {member.skills && member.skills.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            {member.skills.slice(0, 3).map((skill) => (
              <span key={skill} className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#FF2D87] text-white">
                {skill}
              </span>
            ))}
            {member.skills.length > 3 && (
              <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#FF2D87]/30 text-[#FF2D87]">
                +{member.skills.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Goals - Hot pink bubbles (matches Skills) */}
        {member.goals && member.goals.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            {member.goals.slice(0, 2).map((goal) => (
              <span key={goal} className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#FF2D87] text-white">
                {goal}
              </span>
            ))}
          </div>
        )}

        {/* Bio excerpt */}
        {member.bio && (
          <p className="text-xs text-white/60 line-clamp-2 mb-4">{member.bio}</p>
        )}
      </div>

      {/* Footer — always pinned to bottom for consistent alignment */}
      <div className="mt-auto pt-4 flex flex-col items-center gap-3 border-t border-white/10">
        {/* Social Links - Blue outline (always rendered to keep alignment) */}
        <div className="flex gap-2 items-center min-h-[36px]">
          {canSeeContact && (member.contact_email || member.display_email || member.email) ? (
            <a href={`mailto:${member.contact_email || member.display_email || member.email}`}
              className="w-9 h-9 rounded-full border-2 border-[#5170FF] flex items-center justify-center text-[#5170FF] hover:bg-[#5170FF] hover:text-white transition-colors">
              <Mail className="w-4 h-4" />
            </a>
          ) : (
            <button onClick={handleEmailClick}
              className="w-9 h-9 rounded-full border-2 border-[#5170FF]/30 flex items-center justify-center text-[#5170FF]/30 cursor-not-allowed relative group">
              <Mail className="w-4 h-4" />
              <Lock className="w-2.5 h-2.5 absolute -top-1 -right-1 text-[#FF2D87]" />
            </button>
          )}

          {canSeeContact && member.website && (
            <a href={member.website} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border-2 border-[#5170FF] flex items-center justify-center text-[#5170FF] hover:bg-[#5170FF] hover:text-white transition-colors">
              <Globe className="w-4 h-4" />
            </a>
          )}
          {canSeeContact && member.instagram && (
            <a href={`https://instagram.com/${member.instagram}`} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border-2 border-[#5170FF] flex items-center justify-center text-[#5170FF] hover:bg-[#5170FF] hover:text-white transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
          )}
          {canSeeContact && member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border-2 border-[#5170FF] flex items-center justify-center text-[#5170FF] hover:bg-[#5170FF] hover:text-white transition-colors">
              <Linkedin className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* View Profile Link */}
        <Link
          to={currentUser ? `/profile/${member.user_id}` : "/login"}
          className="text-xs font-bold text-[#FFC2E8] hover:text-white flex items-center gap-1 transition-colors"
        >
          View Profile <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export default MemberDirectory;
